/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import type { AnyAction } from '@reduxjs/toolkit';
import { all, call, put, takeEvery } from 'redux-saga/effects';

import * as api from '@ibm-watson/shared/modules/dialog/api/recommendations';
import * as actions from './actions';
import * as actionType from './actionTypes';

export function* fetchRecommendationsExistingValue(action: AnyAction) {
  try {
    const data = yield call(
      api.listSynonymRecommendationsForExistingValue,
      action.workspaceId,
      action.entityId,
      action.valueId,
      action.negativeExamples,
      action.includeDetails,
      action.skillId,
      action.invokeApi
    );
    yield put(actions.addExistingValueRecommendations(data.recommendations));
  } catch (error) {
    yield put(actions.listExistingValueRecommendationsError(error));
  }
}

export function* fetchRecommendationsNewValue(action: AnyAction) {
  try {
    const data = yield call(
      api.listSynonymRecommendationsForNewValue,
      action.workspaceId,
      action.entityId,
      action.valueId,
      action.synonyms,
      action.negativeExamples,
      action.includeDetails,
      action.skillId,
      action.invokeApi
    );
    yield put(actions.addNewValueRecommendations(data.recommendations));
  } catch (error) {
    yield put(actions.listNewValueRecommendationsError(error));
  }
}

export function* listRecommendationSources(action: AnyAction) {
  try {
    const response = yield call(
      api.listRecommendationSources,
      action.workspaceId,
      action.skillId,
      action.showingNewIA,
      action.initialCall
    );
    yield put(
      actions.listRecommendationSourcesSuccess(
        response.data.recommendation_sources,
        response.data.migrated
      )
    );
  } catch (error) {
    yield put(actions.listRecommendationSourcesError(error));
  }
}

export function* setRecommendationSources(action: AnyAction) {
  try {
    const response = yield call(
      api.setRecommendationSources,
      action.workspaceId,
      action.skillId,
      action.sources,
      action.showingNewIA
    );
    yield put(
      actions.setRecommendationSourcesSuccess(
        response.data.recommendation_sources,
        response.status
      )
    );
  } catch (error) {
    yield put(actions.setRecommendationSourcesError(error));
  }
}

function* watchFetchRecommendationsExistingValue() {
  yield takeEvery(
    actionType.FETCH_RECOMMENDATIONS_EXISTING_VALUE,
    fetchRecommendationsExistingValue
  );
}

function* watchFetchRecommendationsNewValue() {
  yield takeEvery(
    actionType.FETCH_RECOMMENDATIONS_NEW_VALUE,
    fetchRecommendationsNewValue
  );
}

function* watchListRecommendationSources() {
  yield takeEvery(
    actionType.FETCH_RECOMMENDATION_SOURCES_START,
    listRecommendationSources
  );
}

function* watchSetRecommendationSources() {
  yield takeEvery(
    actionType.SET_RECOMMENDATION_SOURCES_START,
    setRecommendationSources
  );
}

export default function* rootSaga() {
  yield all([
    watchFetchRecommendationsExistingValue(),
    watchFetchRecommendationsNewValue(),
    watchListRecommendationSources(),
    watchSetRecommendationSources()
  ]);
}
